import { useApolloClient } from '@apollo/client';
import {
  BooleanViewField,
  Details,
  type DetailsProps,
  type FormActionData,
  formatDateTime,
  GenericField,
  getFormDiff,
  IconName,
  InfoPanel,
  type Nullable,
  type ObjectSchemaDefinition,
  Paragraph,
  ReadOnlyField,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { Field, type FormikValues, useFormikContext } from 'formik';
import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  type ServiceAccount,
  ServiceAccountDocument,
  type ServiceAccountQuery,
  type UpdateServiceAccountMutationVariables,
  useDeleteServiceAccountMutation,
  useServiceAccountQuery,
  useSetServiceAccountProtectionMutation,
  useUpdateServiceAccountMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { apolloClient } from '../../../../modules/apolloClient';

const serviceAccountDetailSchema = Yup.object<ObjectSchemaDefinition>({
  name: Yup.string().required('Name is a required field').max(100),
});

type FormData = Nullable<
  UpdateServiceAccountMutationVariables['input']['patch']
>;

const Form: React.FC<{ isSecretGenerated: boolean | null | undefined }> = (
  props,
) => {
  const isSecretGenerated = props.isSecretGenerated;

  return (
    <>
      <Field name="name" label="Name" as={SingleLineTextField} />
      <Field name="clientId" label="Client ID" as={ReadOnlyField} />
      <GenericField label="Client Secret" name="secret">
        {!isSecretGenerated && <p>Not Available</p>}
        {isSecretGenerated && (
          <p>*************************************************</p>
        )}
      </GenericField>
      <Field
        name="isProtected"
        label="Is Protected"
        as={BooleanViewField}
        trueIcon={IconName.Protected}
        falseIcon={IconName.Unprotected}
        trueLabel="Protected"
        falseLabel="Unprotected"
        tooltipContent="While protected, this service account cannot be edited or deleted by anyone."
      />
    </>
  );
};

export const ServiceAccountDetail: React.FC = () => {
  const { envId, id } = useParams<{
    envId: string;
    id: string;
  }>();

  const apolloClient = useApolloClient();

  // Query service account data
  const { loading, data, error, refetch } = useServiceAccountQuery({
    client: apolloClient,
    variables: { id },
    fetchPolicy: 'no-cache',
  });

  const [updateServiceAccount] = useUpdateServiceAccountMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const { actions } = useActions(
    envId,
    id,
    data?.serviceAccount ? data.serviceAccount.isProtected : null,
    refetch,
  );

  const onSubmit = useCallback(
    async (
      formData: FormikValues,
      initialData: DetailsProps<FormData>['initialData'],
    ): Promise<FormData> => {
      await updateServiceAccount({
        variables: {
          input: {
            id,
            patch: getFormDiff(formData, initialData.data),
          },
        },
      });
      return formData;
    },
    [updateServiceAccount, id],
  );

  return (
    <Details<FormData>
      defaultTitle="Service Account"
      titleProperty="name"
      subtitle="Service Account Details"
      actions={actions}
      validationSchema={serviceAccountDetailSchema}
      initialData={{
        data: data?.serviceAccount,
        loading,
        error: error?.message,
      }}
      saveData={onSubmit}
      infoPanel={<Panel />}
    >
      <Form isSecretGenerated={data?.serviceAccount?.isSecretGenerated} />
    </Details>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<ServiceAccount>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Service Account ID">{values.id}</Paragraph>
        <Paragraph title="Created">
          {formatDateTime(values.createdAt)} by {values.createdBy}
        </Paragraph>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

const useActions = (
  envId: string,
  id: string,
  isProtected: boolean | null,
  refetch: () => Promise<unknown>,
): {
  readonly actions: FormActionData<FormData>[];
} => {
  const history = useHistory();
  const apolloClient = useApolloClient();

  const [deleteServiceAccountMutation] = useDeleteServiceAccountMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });
  const [setServiceAccountProtectionMutation] =
    useSetServiceAccountProtectionMutation({
      client: apolloClient,
    });

  const actions: FormActionData<FormData>[] = [
    {
      label: 'Permissions',
      path: `/env/${envId}/serviceAccounts/${id}/permissions`,
    },
    {
      label: 'Client Secret',
      path: `/env/${envId}/serviceAccounts/${id}/clientSecret`,
    },
    {
      label: 'Access Token',
      path: `/env/${envId}/serviceAccounts/${id}/generateToken`,
    },
    {
      label: `Set as ${isProtected ? 'Unprotected' : 'Protected'}`,
      onActionSelected: async () => {
        await setServiceAccountProtectionMutation({
          variables: { input: { id, isProtected: !isProtected } },
        });
        // This query uses fetchPolicy: 'no-cache'
        await refetch();
      },
      isDisabled: isProtected == null,
      confirmationMode: 'Advanced',
      confirmationConfig: {
        title: isProtected ? 'Remove Protection?' : 'Protect This Account?',
        body: isProtected
          ? 'Unprotecting this service account will allow it to be edited or deleted again by users with the relevant permissions.'
          : 'Protecting this service account will prevent anyone from editing or deleting it until it is unprotected.',
      },
    },
    {
      label: 'Delete',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        await deleteServiceAccountMutation({
          variables: { input: { id } },
        });
        history.push(`/env/${envId}/serviceAccounts`);
      },
    },
  ];

  return { actions } as const;
};

export const ServiceAccountDetailCrumb: BreadcrumbResolver = ({ id }) => {
  return async (): Promise<string> => {
    const response = await apolloClient().query<ServiceAccountQuery>({
      query: ServiceAccountDocument,
      variables: { id: id },
      errorPolicy: 'ignore',
    });

    return response.data.serviceAccount?.name || 'Service Accounts Details';
  };
};
